import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { getQuickLinks } from '../api/apiClient';
import socket from '../socket';
import './QuickLinks.css';

const TOKEN_KEY = 'intranet_token';
const openAuthFile = async (url, title = 'Document') => {
  if (!url.startsWith('/pdf/')) { window.open(url, '_blank', 'noopener,noreferrer'); return; }
  const apiUrl = url.replace(/^\/pdf\//, '/api/resources/file/');
  const token = localStorage.getItem(TOKEN_KEY);
  try {
    const res = await fetch(apiUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error('Access denied');
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const html = `<!DOCTYPE html><html><head><title>${title}</title></head><body style="margin:0"><iframe src="${blobUrl}" style="width:100%;height:100vh;border:none"></iframe></body></html>`;
    const htmlBlob = new Blob([html], { type: 'text/html' });
    const htmlUrl = URL.createObjectURL(htmlBlob);
    window.open(htmlUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => { URL.revokeObjectURL(blobUrl); URL.revokeObjectURL(htmlUrl); }, 30000);
  } catch { /* silently fail */ }
};

// ── Icons ────────────────────────────────────────────────────────────────────
const Ico = ({ children }) => (
  <svg className="ql-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

export const ICONS = {
  // ── App-specific ─────────────────────────────────────────────────────────────
  clio:         <Ico><circle cx="12" cy="12" r="3"/><path d="M12 2a10 10 0 1 0 7.39 16.74"/><path d="M22 2 12 12"/></Ico>,
  sharepoint:   <Ico><circle cx="18" cy="5" r="2"/><circle cx="6" cy="12" r="2"/><circle cx="18" cy="19" r="2"/><path d="m8 11.5 6-3M8 12.5l6 3"/></Ico>,
  linkedin: (
    <svg className="ql-ico" viewBox="0 0 448 512" fill="currentColor">
      <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3C448 46.5 433.6 32 416 32zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"/>
    </svg>
  ),

  // ── Documents & Files ────────────────────────────────────────────────────────
  book:         <Ico><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><path d="M8 7h8M8 11h5"/></Ico>,
  billing:      <Ico><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M9 7h6M9 12h6M9 17h4"/><path d="M15 7v1a2 2 0 0 1-4 0V7"/></Ico>,
  fileplus:     <Ico><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></Ico>,
  folder:       <Ico><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></Ico>,
  file:         <Ico><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/></Ico>,
  help:         <Ico><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><path d="M9.09 11a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="18" x2="12.01" y2="18"/></Ico>,
  clipboard:    <Ico><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M9 12h6M9 16h4"/></Ico>,
  archive:      <Ico><polyline points="21,8 21,21 3,21 3,8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></Ico>,
  download:     <Ico><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></Ico>,
  upload:       <Ico><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17,8 12,3 7,8"/><line x1="12" y1="3" x2="12" y2="15"/></Ico>,
  printer:      <Ico><polyline points="6,9 6,2 18,2 18,9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></Ico>,
  copy:         <Ico><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></Ico>,
  pen:          <Ico><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></Ico>,
  image:        <Ico><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></Ico>,

  // ── Communication ────────────────────────────────────────────────────────────
  mail:         <Ico><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></Ico>,
  phone:        <Ico><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.61 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></Ico>,
  video:        <Ico><polygon points="23,7 16,12 23,17"/><rect x="1" y="5" width="15" height="14" rx="2"/></Ico>,
  bell:         <Ico><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></Ico>,
  send:         <Ico><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22,2 15,22 11,13 2,9"/></Ico>,
  inbox:        <Ico><polyline points="22,12 16,12 14,15 10,15 8,12 2,12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></Ico>,
  link:         <Ico><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></Ico>,
  externallink: <Ico><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15,3 21,3 21,9"/><line x1="10" y1="14" x2="21" y2="3"/></Ico>,

  // ── People ───────────────────────────────────────────────────────────────────
  user:         <Ico><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></Ico>,
  users:        <Ico><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Ico>,
  usercheck:    <Ico><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17,11 19,13 23,9"/></Ico>,

  // ── Legal & Business ─────────────────────────────────────────────────────────
  shield:       <Ico><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></Ico>,
  briefcase:    <Ico><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="12.01"/><path d="M2 12h9M13 12h9"/></Ico>,
  scale:        <Ico><line x1="12" y1="3" x2="12" y2="21"/><path d="M3 6h9M12 6h9"/><path d="M3 6l3 9M6 15H3"/><path d="M21 6l-3 9M18 15h3"/><path d="M4 21h16"/></Ico>,
  gavel:        <Ico><path d="m14 13-8.5 8.5a2.12 2.12 0 0 1-3-3L11 10"/><path d="m16 16 6-6"/><path d="m8 8 6-6"/><path d="m9 7 8 8"/><path d="m21 11-8-8"/></Ico>,
  building:     <Ico><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M8 10h.01M8 14h.01M16 10h.01M16 14h.01"/></Ico>,
  award:        <Ico><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></Ico>,

  // ── Finance ──────────────────────────────────────────────────────────────────
  dollar:       <Ico><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></Ico>,
  creditcard:   <Ico><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></Ico>,
  tag:          <Ico><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></Ico>,
  barchart:     <Ico><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></Ico>,
  trending:     <Ico><polyline points="23,6 13.5,15.5 8.5,10.5 1,18"/><polyline points="17,6 23,6 23,12"/></Ico>,

  // ── Navigation & Location ────────────────────────────────────────────────────
  home:         <Ico><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/></Ico>,
  globe:        <Ico><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></Ico>,
  mappin:       <Ico><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></Ico>,
  compass:      <Ico><circle cx="12" cy="12" r="10"/><polygon points="16.24,7.76 14.12,14.12 7.76,16.24 9.88,9.88"/></Ico>,

  // ── Time ─────────────────────────────────────────────────────────────────────
  clock:        <Ico><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></Ico>,
  calendar:     <Ico><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Ico>,

  // ── Status & Interaction ─────────────────────────────────────────────────────
  star:         <Ico><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/></Ico>,
  bookmark:     <Ico><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></Ico>,
  flag:         <Ico><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></Ico>,
  heart:        <Ico><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></Ico>,
  checkcircle:  <Ico><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22,4 12,14.01 9,11.01"/></Ico>,
  alerttriangle:<Ico><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></Ico>,
  infocircle:   <Ico><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></Ico>,

  // ── Tools & Settings ────────────────────────────────────────────────────────
  settings:     <Ico><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></Ico>,
  key:          <Ico><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4"/></Ico>,
  lock:         <Ico><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></Ico>,
  lockopen:     <Ico><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></Ico>,
  tool:         <Ico><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></Ico>,
  filter:       <Ico><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"/></Ico>,
  eye:          <Ico><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></Ico>,
  trash:        <Ico><polyline points="3,6 5,6 21,6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></Ico>,

  // ── Technology ───────────────────────────────────────────────────────────────
  search:       <Ico><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Ico>,
  layout:       <Ico><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></Ico>,
  bulb:         <Ico><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></Ico>,
  database:     <Ico><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></Ico>,
  server:       <Ico><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></Ico>,
  monitor:      <Ico><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></Ico>,
  wifi:         <Ico><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></Ico>,
  cloud:        <Ico><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></Ico>,
  code:         <Ico><polyline points="16,18 22,12 16,6"/><polyline points="8,6 2,12 8,18"/></Ico>,
  zap:          <Ico><polygon points="13,2 3,14 12,14 11,22 21,10 12,10"/></Ico>,
  activity:     <Ico><polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/></Ico>,
  smartphone:   <Ico><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></Ico>,

  // ── internal ─────────────────────────────────────────────────────────────────
  chevron:      <Ico><polyline points="6,9 12,15 18,9"/></Ico>,
};

export const ICON_KEYS = Object.keys(ICONS).filter(k => k !== 'chevron');

export const SECTION_ORDER = ['attorney', 'marketing', 'general'];
export const SECTION_LABELS = {
  attorney: 'Attorney Resources',
  marketing: 'Marketing',
  general: 'General',
};

export const BUILTIN_MARKETING = [];

// Cards that open a small text popup (no link) rather than navigating.
export const BUILTIN_GENERAL = [
  { title: 'IT Helpdesk Contact', icon: 'phone', text: 'LaSalle Consulting Partners: 312-361-3326 (Option 1)' },
];

const SECTION_BUILTINS = { marketing: BUILTIN_MARKETING, general: BUILTIN_GENERAL };

// ── Card components ───────────────────────────────────────────────────────────
const LinkCard = ({ title, url, icon }) => {
  if (url && url.startsWith('/pdf/')) {
    return (
      <button className="ql-card" onClick={() => openAuthFile(url, title)}>
        {ICONS[icon] || ICONS.folder}
        <span>{title}</span>
      </button>
    );
  }
  if (url && url.startsWith('/')) {
    return (
      <Link className="ql-card" to={url}>
        {ICONS[icon] || ICONS.folder}
        <span>{title}</span>
      </Link>
    );
  }
  return (
    <a className="ql-card" href={url} target="_blank" rel="noopener noreferrer">
      {ICONS[icon] || ICONS.folder}
      <span>{title}</span>
    </a>
  );
};

const DropCard = ({ title, icon, options, open, onToggle }) => {
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const [coords, setCoords] = useState(null);

  const reposition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({ top: rect.bottom + 6, left: rect.left, width: rect.width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      onToggle();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onToggle]);

  return (
    <div className="ql-drop-wrap" ref={wrapRef}>
      <button className={`ql-card ql-card--drop ${open ? 'ql-card--open' : ''}`} onClick={onToggle}>
        {ICONS[icon] || ICONS.folder}
        <span>{title}</span>
        <svg className="ql-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6,9 12,15 18,9"/>
        </svg>
      </button>
      {open && coords && createPortal(
        <div
          className="ql-drop-menu"
          ref={menuRef}
          style={{ top: coords.top, left: coords.left, minWidth: coords.width }}
        >
          {options.map(opt => opt.url && opt.url.startsWith('/pdf/') ? (
            <button key={opt.label} className="ql-drop-item" onClick={() => openAuthFile(opt.url, opt.label)}>
              {opt.label}
            </button>
          ) : (
            <a key={opt.label} className="ql-drop-item" href={opt.url}
              target="_blank" rel="noopener noreferrer">
              {opt.label}
            </a>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// Like DropCard, but clicking reveals a plain text popup (e.g. a phone number)
// instead of a menu of links.
const InfoCard = ({ title, icon, text, open, onToggle }) => {
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const [coords, setCoords] = useState(null);

  const reposition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({ top: rect.bottom + 6, left: rect.left, width: rect.width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      onToggle();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onToggle]);

  return (
    <div className="ql-drop-wrap" ref={wrapRef}>
      <button className={`ql-card ql-card--drop ${open ? 'ql-card--open' : ''}`} onClick={onToggle}>
        {ICONS[icon] || ICONS.infocircle}
        <span>{title}</span>
        <svg className="ql-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6,9 12,15 18,9"/>
        </svg>
      </button>
      {open && coords && createPortal(
        <div
          className="ql-drop-menu ql-info-menu"
          ref={menuRef}
          style={{ top: coords.top, left: coords.left, minWidth: Math.max(coords.width, 220) }}
        >
          <p className="ql-info-text">{text}</p>
        </div>,
        document.body
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const QuickLinks = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDrop, setOpenDrop] = useState(null);
  const toggle = key => setOpenDrop(prev => prev === key ? null : key);

  const fetchLinks = useCallback(() => {
    getQuickLinks()
      .then(res => setLinks(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchLinks();
    socket.on('links:changed', fetchLinks);
    return () => socket.off('links:changed', fetchLinks);
  }, [fetchLinks]);

  if (loading) return null;

  const sections = SECTION_ORDER
    .map(key => ({ key, label: SECTION_LABELS[key], items: links.filter(l => l.category === key) }))
    .filter(section => section.items.length > 0 || (SECTION_BUILTINS[section.key] || []).length > 0);

  return (
    <div className="ql-outer">
      <h2 className="ql-heading">Quick Links</h2>
      <div className="ql-wrapper">
        <div className="ql-body">
          {sections.map((section, si) => (
            <div key={section.key} className="ql-section" style={{ animationDelay: `${si * 60}ms` }}>
              <div className="ql-label">{section.label}</div>
              <div className="ql-cards">
                {section.key === 'marketing' && BUILTIN_MARKETING.map(app => (
                  <Link key={app.path} to={app.path} className="ql-card">
                    {ICONS[app.icon] || ICONS.folder}
                    <span>{app.title}</span>
                  </Link>
                ))}
                {section.items.map(item =>
                  item.options && item.options.length > 0 ? (
                    <DropCard
                      key={item.id}
                      title={item.title}
                      icon={item.icon}
                      options={item.options}
                      open={openDrop === item.id}
                      onToggle={() => toggle(item.id)}
                    />
                  ) : (
                    <LinkCard key={item.id} title={item.title} url={item.url} icon={item.icon} />
                  )
                )}
                {section.key === 'general' && BUILTIN_GENERAL.map(item => (
                  <InfoCard
                    key={item.title}
                    title={item.title}
                    icon={item.icon}
                    text={item.text}
                    open={openDrop === `bg-${item.title}`}
                    onToggle={() => toggle(`bg-${item.title}`)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuickLinks;
